import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    EntityValidations, ISpecificEntityFormProps, IApp03WorkflowStage, IApp03Task, App03Task, IUser, App03TaskUser,
    App03TaskObserver,
    App03TaskUserGroupsTemp,
    SaveStatus,
    IApp03Sprint,
    IApp03Epic,
    CustomField,
    IApp03WorkItemType
} from '../../types';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { DataContext } from '../DataContext';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrDatePicker } from '../cr/CrDatePicker';
import { TaskPriority } from '../../refData/WorkflowType';
import { EntityFormPage } from '../EntityFormPage';
import useMobileDetector from '../useMobileDetector';
import { CrCommandBar } from '../cr/CrCommandBar';
import EvidenceList from './EV/EvidenceList';
import { OrbUserContext } from '../OrbUserContext';
import { App03TaskAssignMethod, App03TaskAssignMethodLabels } from '../../types/AppGlobals';
import { App03TaskTimeOptionKanban, App03TaskTimeOptionScrum } from '../../types/App03Task';
import { App03Frameworks } from '../../types/App03Workflow';
import { ISelectableOption } from '@fluentui/react';
import { SprintFilterCategoryKeys } from './WorkflowTasks';
import { App03SprintStatus } from '../../types/App03Sprint';
import { EntityList } from '../EntityList';
import { SubTaskForm } from './SubTaskForm';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';

class TaskFormValidations extends EntityValidations {
    public AssignedTo: string = null;
    public WorkItemType: string = null;
}

interface ITaskFormProps extends ISpecificEntityFormProps {
    workflowID: number;
    workflowTemplateID?: number;
    workflowTitle?: string;
    newTaskStageID: number;
    frameworkID: number;
    sprintFilterCategory?: string;
    epicID: number;
    taskTimeOption?: string;
    defaultPlanningStrategy?: string;
    assignedToDefaultUserID?: number;

}

export const getPlanningStrategyOptions = (frameworkID: App03Frameworks) => {
    const kanbanOptions = [
        { ID: 'Priority', Title: 'Based on Priority' },
        { ID: 'TargetDates', Title: 'Based on Target Dates' },
        { ID: 'CriticalDueDate', Title: 'Based on Critical Due Date' },
    ];

    const scrumOptions = [
        { ID: 'Priority', Title: 'Based on Sprint' },
        { ID: 'TargetDates', Title: 'Based on Sprint plus Target Dates' },
        { ID: 'CriticalDueDate', Title: 'Based on Sprint and Critical Due Date' },
    ];

    const options =
        frameworkID === App03Frameworks.Scrum
            ? scrumOptions
            : kanbanOptions;

    return LookupService.entitiesToSelectableOptionsCustom(options);
};

export const TaskFormPage = (props: ITaskFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { zCoreUserGroups, entityStatuses, customFieldDfns /*users: { enabled: enabledUsers }*/ } } = useContext(DataContext);
    const { userPermissions } = useContext(OrbUserContext);
    const { userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const isMobile = useMobileDetector();
    useMemo(() => entityStatuses(), [entityStatuses]);
    //useMemo(() => enabledUsers(), [enabledUsers]);
    useMemo(() => zCoreUserGroups(), [zCoreUserGroups]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);

    const [workItemTypes, setWorkItemTypes] = useState<IApp03WorkItemType[]>([]);
    const [workflowStages, setWorkflowStages] = useState<IApp03WorkflowStage[]>([]);
    const [workflowUsers, setWorkflowUsers] = useState<IUser[]>([]);
    const [workflowSprints, setWorkflowSprints] = useState<IApp03Sprint[]>([]);
    const [workflowEpics, setWorkflowEpics] = useState<IApp03Epic[]>([]);
    const [showArchived, setShowArchived] = useState<boolean>(true);
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');
    const [taskInfo, setTaskInfo] = useState<IApp03Task>();
    const [taskAddedOn, setTaskAddedOn] = useState<string>('');

    const changeHandlersRef = useRef<any>(null);
    const [defaultSprintID, setDefaultSprintID] = useState<number>(null);
    const [defaultWorkItemTypeID, setDefaultWorkItemTypeID] = useState<number>(null);

    useEffect(() => {

        const fetchWorkItemTypes = async () => {
            try {
                const workItemTypes = await dataServices.app03WorkItemTypeService.readAllForDropList(props.workflowTemplateID);
                setWorkItemTypes(workItemTypes);

                const defaultWorkItemType = workItemTypes.find(x => x.IsDefault === true)?.ID ?? null;
                setDefaultWorkItemTypeID(defaultWorkItemType);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchStages = async () => {
            try {
                const stages = await dataServices.app03WorkflowStageService.readWorkflowStages(props.workflowID);
                setWorkflowStages(stages);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchWorkflowUsers = async () => {
            try {
                const users = (await dataServices.app03WorkflowUserService.readWorkflowUsers(props.workflowID)).map(u => u.User);
                setWorkflowUsers(users);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchWorkflowSprints = async () => {
            if (props.frameworkID === App03Frameworks.Scrum) {
                try {
                    const sprints = (await dataServices.app03SprintService.readAllForAddTaskDropdown(props.workflowID));
                    setWorkflowSprints(sprints);

                    if (props.sprintFilterCategory === SprintFilterCategoryKeys.CurrentSprint) {
                        const activeSprintID = sprints.find(s => s.SprintStatus === App03SprintStatus.Active)?.ID ?? null;
                        setDefaultSprintID(activeSprintID);
                    }

                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            }

        };

        const fetchWorkflowEpics = async () => {
            try {
                const epics = (await dataServices.app03EpicService.readAllForAddTaskDropdown(props.workflowID));
                setWorkflowEpics(epics);

            } catch (error) {
                console.error('Error fetching data:', error);
            }


        };

        const fetchTaskInfo = async () => {
            if (props.entityId > 0) {
                try {
                    const task = await dataServices.app03TaskService.readForView(props.entityId);
                    //console.log('taskInfo', task);
                    setTaskInfo(task);

                    if (task?.['AddedOn'] !== null) {
                        const addedOn = DateService.dateToUkDateTime(new Date(new Date(task?.['AddedOn']).toLocaleString()));
                        setTaskAddedOn(addedOn);
                    }

                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            } else {
                setTaskAddedOn(DateService.dateToUkDateTime(new Date()));
            }
        };

        fetchWorkItemTypes();
        fetchWorkflowUsers();
        fetchWorkflowSprints();
        fetchWorkflowEpics();
        fetchStages();
        fetchTaskInfo();
    }, [props.entityId]);


    useEffect(() => {
        if (props.entityId === null) {
            if (defaultWorkItemTypeID && changeHandlersRef.current) {
                const { changeDropdown, getFormData } = changeHandlersRef.current;
                const formData = getFormData();
                if (!formData.App03WorkItemTypeID) {
                    changeDropdown({ key: defaultWorkItemTypeID }, 'App03WorkItemTypeID');
                }
            }
        }

    }, [defaultWorkItemTypeID]);

    useEffect(() => {
        if (props.frameworkID === App03Frameworks.Scrum && props.entityId === null) {
            if (defaultSprintID && changeHandlersRef.current) {
                const { changeDropdown, getFormData } = changeHandlersRef.current;
                const formData = getFormData();
                if (!formData.SprintID) {
                    changeDropdown({ key: defaultSprintID, text: '' }, 'SprintID');
                }
            }
        }

    }, [defaultSprintID]);

    useEffect(() => {
        if (props.entityId === null && changeHandlersRef.current) {
            const { changeDropdown, changeMultiUserPicker, getFormData } = changeHandlersRef.current;
            const formData = getFormData();

            if (props.assignedToDefaultUserID && props.assignedToDefaultUserID > 0) {
                // Set Assign Method to "One task assigned to all user(s)"
                if (formData.AssignMethodID !== App03TaskAssignMethod.OneTaskForAll) {
                    changeDropdown({ key: App03TaskAssignMethod.OneTaskForAll }, 'AssignMethodID');
                }
                // Set the default user to Assigned To field
                changeMultiUserPicker(
                    [props.assignedToDefaultUserID],
                    'App03TaskUsers',
                    new App03TaskUser(),
                    'UserID'
                );
            } else {
                // Set Assign Method to "Assign Later"
                if (formData.AssignMethodID !== App03TaskAssignMethod.AssignLater) {
                    changeDropdown({ key: App03TaskAssignMethod.AssignLater }, 'AssignMethodID');
                }
            }
        }

    }, [props.assignedToDefaultUserID, props.entityId]);


    const getSprintDropdownOptions = (): ISelectableOption[] => {
        const options = workflowSprints.map(s => ({
            key: s.ID,
            text: `Sprint ${s.SprintNumber} - ${s.Title}`,
        }));

        // Add a blank or placeholder option at the top
        return [{ key: null, text: '' }, ...options];
    };

    const getEpicDropdownOptions = (): ISelectableOption[] => {
        const options = workflowEpics.map(s => ({
            key: s.ID,
            text: s.Title,
        }));

        // Add a blank or placeholder option at the top
        return [{ key: null, text: '' }, ...options];
    };


    const validateEntity = (task: IApp03Task): Promise<TaskFormValidations> => {
        const errors = new TaskFormValidations();

        if (task.Title === null || task.Title === '') {
            errors.Title = `Work Item name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        if (task.App03WorkItemTypeID === null) {
            errors.WorkItemType = `Type is required`;
            errors.Valid = false;
        } else {
            errors.WorkItemType = null;
        }

        if (task.ID == 0 && (task.AssignMethodID === App03TaskAssignMethod.OneTaskForAll || task.AssignMethodID === App03TaskAssignMethod.SeparateTaskForEachUser) && (task.App03TaskUsers === undefined || task.App03TaskUsers === null || task.App03TaskUsers.length < 1)) {
            errors.AssignedTo = `At least one assignee is required`;
            errors.Valid = false;
        }
        else if ((task.AssignMethodID === App03TaskAssignMethod.SeparateTaskForEachUserInGroup) && (task.App03TaskUserGroupsTemps === undefined || task.App03TaskUserGroupsTemps === null || task.App03TaskUserGroupsTemps.length < 1)) {
            errors.AssignedTo = `At least one user group is required`;
            errors.Valid = false;
        }
        else {
            errors.AssignedTo = null;
        }


        return Promise.resolve(errors);
    };

    const isViewOnlyPermission = (): boolean => {
        return false;
    }

    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    const filteredCustomFieldDfns = useMemo(() => {
        const appPrefix = props.frameworkID === App03Frameworks.Scrum ? "Agile Sprints - " : "Group Workstreams - ";
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                `${appPrefix}Work Items`,
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    const taskPriorityArray = Object.keys(TaskPriority)
        .filter(key => !isNaN(Number(TaskPriority[key])))
        .map(key => ({ ID: TaskPriority[key], Title: key }));

    return (
        <>
            <EntityFormPage<IApp03Task, TaskFormValidations>
                {...props}
                entityName='Task'
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { FormData: formData, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                    changeHandlersRef.current = {
                        ...changeHandlers,
                        getFormData: () => formData,
                    };

                    let commandBarItems: ICommandBarItemProps[] = [];

                    if (FormSaveStatus === SaveStatus.Pending) {
                        commandBarItems.push({
                            key: 'Save',
                            text: 'Saving...',
                            iconProps: { iconName: 'Sync' },
                            disabled: true,
                        });
                    } else if (!FormIsDirty) {
                        commandBarItems.push({
                            key: 'Close',
                            text: 'Close',
                            iconProps: { iconName: 'Cancel' },
                            onClick: (ev, item) => {
                                props.onCancelled();
                            },
                        });
                    } else {
                        commandBarItems.push(
                            {
                                key: 'Save',
                                text: 'Save',
                                iconProps: { iconName: 'Save' },
                                onClick: (ev, item) => {
                                    saveEntity();
                                },
                            },
                            {
                                key: 'Cancel',
                                text: 'Cancel',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    props.onCancelled();
                                },
                            }
                        );
                    }

                    const getPlannedTimeLabelText = () => {
                        if (props.frameworkID === App03Frameworks.Kanban) {
                            if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInHours)
                                return "Planned Time in Hours";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInDays)
                                return "Planned Time in Days";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInDifficultyPoints)
                                return "Planned Time in Difficulty Points";
                            else
                                return "Planned Time";
                        } else if (props.frameworkID === App03Frameworks.Scrum) {
                            if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseStoryPoints)
                                return "Planned Time in Story Points";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseHours)
                                return "Planned Time in Hours";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseDays)
                                return "Planned Time in Days";
                            else
                                return "Planned Time";
                        }
                        return "Planned Time";
                    };

                    const getActualTimeLabelText = () => {
                        if (props.frameworkID === App03Frameworks.Kanban) {
                            if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInHours)
                                return "Actual Time in Hours";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInDays)
                                return "Actual Time in Days";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionKanban.RequiredInDifficultyPoints)
                                return "Actual Time in Difficulty Points";
                            else
                                return "Actual Time";
                        } else if (props.frameworkID === App03Frameworks.Scrum) {
                            if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseStoryPoints)
                                return "Actual Time in Story Points";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseHours)
                                return "Actual Time in Hours";
                            else if (formData.TaskTimeOption === App03TaskTimeOptionScrum.UseDays)
                                return "Actual Time in Days";
                            else
                                return "Actual Time";
                        }
                        return "Actual Time";
                    };

                    const hasActualTimeline =
                        formData.ID > 0 &&
                        (formData.ActualStartDate || formData.ActualEndDate);

                    let actualTimelineText = '';

                    if (formData.ActualStartDate && formData.ActualEndDate) {
                        actualTimelineText = `Started on ${DateService.dateToUkLongDate(formData.ActualStartDate)} and Completed on ${DateService.dateToUkLongDate(formData.ActualEndDate)}`;
                    }
                    else if (formData.ActualStartDate) {
                        actualTimelineText = `Started on ${DateService.dateToUkLongDate(formData.ActualStartDate)}`;
                    }
                    else if (formData.ActualEndDate) {
                        actualTimelineText = `Completed on ${DateService.dateToUkLongDate(formData.ActualEndDate)}`;
                    }


                    //Labels row 1
                    const workItemTitleLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Work Item</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const workItemTypeLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Type</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 3
                    const stageLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Stage</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const priotityLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Priority</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 4
                    const targetStartDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Target Start Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const targetEndDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Target Completion Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const criticalDueDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Critical Due Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 5
                    const taskTimeOptionLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Time planned and actual</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const sprintLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Sprint</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 6
                    const plannedTimeLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}>
                                    <span>{getPlannedTimeLabelText()}</span>
                                </div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqCostCentreValidationImg} /> */}
                                </div>
                            </div>
                        </div>
                    );

                    const actualTimeLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}>
                                    <span>{getActualTimeLabelText()}</span>
                                </div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqDirectorateIdValidationImg} /> */}
                                </div>
                            </div>
                        </div>
                    );

                    //Labels row 5
                    const taskManagerLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Work Item Manager</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const taskPanelLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Work Item Panel</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const epicLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Epic</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 5
                    const assignMethodLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Assign Method</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 6
                    const assignedToLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Assigned To</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    // const externalRefLabel = <div className='two-fields-sidebyside-lbl'>
                    //     <div className={styles.flexContainerSectionQuestion}>
                    //         <div className={styles.sectionQuestionCol1}><span>External Ref</span></div>
                    //         <div className={styles.sectionQuestionCol2}>
                    //             {/* <img src={reqDirectorateIdValidationImg} /> */}
                    //         </div>
                    //     </div>
                    // </div>;

                    const planningStrategyLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Planning Strategy</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    return (
                        <div>
                            {/* <CrLoadingOverlay isLoading={isLoading} opaque={true} />*/}
                            <CrCommandBar items={commandBarItems} />
                            <div className='tasks-info'>
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <div className={styles.sectionATitle}>
                                        {props.entityId > 0 ? taskInfo?.Title : 'New Work Item'}
                                    </div>
                                </div>
                            </div>
                            <div className="info-table-container">
                                <table className="info-table" cellSpacing="0" cellPadding="10">
                                    <tbody>
                                        <tr>
                                            <td className="label">
                                                Workstream
                                            </td>
                                            <td className="value">
                                                {props.workflowTitle}
                                            </td>
                                            <td className="label">
                                                Added By
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.['AddedBy']?.['Title']
                                                    : ''
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="label">
                                                Stage
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.WorkflowStage?.Title
                                                    : workflowStages?.find(s => s.ID === props.newTaskStageID)?.Title || ''}

                                            </td>
                                            <td className="label">
                                                Added On
                                            </td>
                                            <td className="value">
                                                {taskAddedOn}
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>
                            </div>

                            {/* Form Fields */}
                            <div>
                                {/* Details section */}
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Details</div>
                                    </div>
                                </div>
                                {
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {workItemTitleLabel}
                                                {workItemTypeLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && workItemTitleLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrTextField
                                                        maxLength={300}
                                                        value={formData.Title}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                                                        errorMessage={errors.Title}
                                                        required={true}
                                                    />
                                                </div>
                                                {isMobile && workItemTypeLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(workItemTypes)}
                                                        selectedKey={formData.App03WorkItemTypeID}
                                                        onChange={(_, o) => changeHandlers.changeDropdown(o, 'App03WorkItemTypeID')}
                                                        required={true}
                                                        errorMessage={errors.WorkItemType}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Details</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrTextField
                                                        multiline
                                                        rows={6}
                                                        maxLength={10000}
                                                        value={formData.Details}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Details')}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 3rd row */}

                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {stageLabel}
                                                {epicLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && stageLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(workflowStages)}
                                                        selectedKey={formData.WorkflowStageID}
                                                        onChange={(_, o) => changeHandlers.changeDropdown(o, 'WorkflowStageID')}
                                                    />
                                                </div>
                                                {isMobile && epicLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={getEpicDropdownOptions()}
                                                        selectedKey={formData.EpicID}
                                                        onChange={(_, o) => changeHandlers.changeDropdown(o, 'EpicID')}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 4th row */}
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {taskManagerLabel}
                                                {taskPanelLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && taskManagerLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrUserPicker
                                                        users={workflowUsers}
                                                        selectedUsers={formData.TaskManagerUserID && [formData.TaskManagerUserID]}
                                                        onChange={v => changeHandlers.changeUserPicker(v, 'TaskManagerUserID')}
                                                    />
                                                </div>
                                                {isMobile && taskPanelLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrUserPicker
                                                        users={workflowUsers}
                                                        itemLimit={10}
                                                        selectedUsers={formData.App03TaskObservers?.map(u => u.UserID)}
                                                        onChange={v => changeHandlers.changeMultiUserPicker(v, 'App03TaskObservers', new App03TaskObserver(), 'UserID')}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 5th row - add mode only - sprint */}
                                        {props.frameworkID === App03Frameworks.Scrum && formData.ID === 0 && (
                                            <div className={styles.formField}>
                                                <div style={{ display: 'flex' }}>
                                                    <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                        <div className={styles.flexContainerSectionQuestion}>
                                                            <div className={styles.sectionQuestionCol1}><span>Sprint</span></div>
                                                            <div className={styles.sectionQuestionCol2}>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', marginTop: '5px' }}>
                                                    <div style={{ width: '100%' }}>
                                                        <CrDropdown
                                                            options={getSprintDropdownOptions()}
                                                            selectedKey={formData.SprintID}
                                                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'SprintID')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* 5th row - add mode only - assign */}
                                        {formData.ID === 0 && (
                                            <div className={styles.formField}>
                                                {!isMobile && (
                                                    <div className='two-fields-sidebyside-r1'>
                                                        {assignMethodLabel}
                                                        {(
                                                            [App03TaskAssignMethod.OneTaskForAll,
                                                            App03TaskAssignMethod.SeparateTaskForEachUser,
                                                            App03TaskAssignMethod.SeparateTaskForEachUserInGroup
                                                            ].includes(formData.AssignMethodID)
                                                        ) && assignedToLabel}
                                                    </div>
                                                )}

                                                <div className='two-fields-sidebyside-r2'>
                                                    <>
                                                        {isMobile && assignMethodLabel}

                                                        <div className='two-fields-sidebyside-field'>
                                                            <CrDropdown
                                                                options={LookupService.entitiesToSelectableOptions(
                                                                    Object.entries(App03TaskAssignMethodLabels).map(([key, value]) => ({
                                                                        ID: Number(key),
                                                                        Title: value,
                                                                    }))
                                                                )}
                                                                selectedKey={formData.AssignMethodID}
                                                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'AssignMethodID')}
                                                            />
                                                        </div>
                                                    </>

                                                    {isMobile && formData.AssignMethodID !== App03TaskAssignMethod.AssignLater && assignedToLabel}

                                                    <div className='two-fields-sidebyside-field'>
                                                        {(
                                                            [App03TaskAssignMethod.OneTaskForAll,
                                                            App03TaskAssignMethod.SeparateTaskForEachUser
                                                            ].includes(formData.AssignMethodID)
                                                        ) && (
                                                                <CrUserPicker
                                                                    users={workflowUsers}
                                                                    itemLimit={10}
                                                                    selectedUsers={formData.App03TaskUsers?.map(u => u.UserID)}
                                                                    onChange={v =>
                                                                        changeHandlers.changeMultiUserPicker(
                                                                            v,
                                                                            'App03TaskUsers',
                                                                            new App03TaskUser(),
                                                                            'UserID'
                                                                        )
                                                                    }
                                                                    required={true}
                                                                    errorMessage={errors.AssignedTo}
                                                                />
                                                            )}

                                                        {formData.AssignMethodID === App03TaskAssignMethod.SeparateTaskForEachUserInGroup && (
                                                            <CrDropdown
                                                                multiSelect
                                                                options={LookupService.entitiesToSelectableOptions(lookupData.ZCoreUserGroups)}
                                                                selectedKeys={formData.App03TaskUserGroupsTemps?.map(tu => tu.ZCoreUserGroupID)}
                                                                onChange={(_, v) =>
                                                                    changeHandlers.changeMultiDropdown(
                                                                        v,
                                                                        'App03TaskUserGroupsTemps',
                                                                        new App03TaskUserGroupsTemp(),
                                                                        'ZCoreUserGroupID'
                                                                    )
                                                                }
                                                                required={true}
                                                                errorMessage={errors.AssignedTo}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* 5th row - edit mode only */}
                                        {formData.ID > 0 && (
                                            <div className={styles.formField}>
                                                {/* Row 1: Labels */}
                                                {!isMobile && (
                                                    <div className='two-fields-sidebyside-r1'>
                                                        {props.frameworkID === App03Frameworks.Scrum && sprintLabel}
                                                        {assignedToLabel}
                                                    </div>
                                                )}

                                                {/* Row 2: Fields */}
                                                <div className='two-fields-sidebyside-r2'>
                                                    {/* Sprint (Left) - Scrum only */}
                                                    {props.frameworkID === App03Frameworks.Scrum && (
                                                        <>
                                                            {isMobile && sprintLabel}
                                                            <div className='two-fields-sidebyside-field'>
                                                                <CrDropdown
                                                                    options={getSprintDropdownOptions()}
                                                                    selectedKey={formData.SprintID}
                                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'SprintID')}
                                                                />
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* Assigned To (Right) - Scrum + Kanban */}
                                                    <>
                                                        {isMobile && assignedToLabel}
                                                        <div className='two-fields-sidebyside-field'>
                                                            <CrUserPicker
                                                                users={workflowUsers}
                                                                itemLimit={10}
                                                                selectedUsers={formData.App03TaskUsers?.map(u => u.UserID)}
                                                                onChange={v =>
                                                                    changeHandlers.changeMultiUserPicker(
                                                                        v,
                                                                        'App03TaskUsers',
                                                                        new App03TaskUser(),
                                                                        'UserID'
                                                                    )
                                                                }
                                                                required={true}
                                                                errorMessage={errors.AssignedTo}
                                                            />
                                                        </div>
                                                        {/* Empty field column for desktop alignment (GW app) */}
                                                        {props.frameworkID === App03Frameworks.Kanban && !isMobile && <div className='two-fields-sidebyside-field'></div>}
                                                    </>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                }

                                {/* Planning section */}
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Planning</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>

                                    {/* Row 1 */}
                                    <div className={styles.formField}>
                                        {!isMobile && (
                                            <div className='two-fields-sidebyside-r1'>
                                                {planningStrategyLabel}
                                                {priotityLabel}
                                            </div>
                                        )}

                                        <div className='two-fields-sidebyside-r2'>
                                            {/* Planning Strategy (Left) */}
                                            {isMobile && planningStrategyLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={getPlanningStrategyOptions(props.frameworkID)}
                                                    selectedKey={formData.PlanningStrategy}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'PlanningStrategy')}
                                                />
                                            </div>

                                            {/* Priority (Right) */}
                                            {isMobile && priotityLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(taskPriorityArray)}
                                                    selectedKey={formData.TaskPriorityID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'TaskPriorityID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 2 */}
                                    {formData.PlanningStrategy !== 'Priority' && <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {targetStartDateLabel}
                                            {targetEndDateLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && targetStartDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.TargetStartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'TargetStartDate')}
                                                />
                                            </div>
                                            {isMobile && targetEndDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.TargetEndDate)}
                                                    minDate={DateService.removeTimezoneOffset(formData.TargetStartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'TargetEndDate')}
                                                //errorMessage={errors.EndDate}
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                    {/* Row 3 */}
                                    {formData.PlanningStrategy === 'CriticalDueDate' && (
                                        <div className={styles.formField}>
                                            {!isMobile && (
                                                <div className='two-fields-sidebyside-r1'>
                                                    {criticalDueDateLabel}
                                                    <div className='two-fields-sidebyside-lbl'></div> {/* empty label column */}
                                                </div>
                                            )}

                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && criticalDueDateLabel}

                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDatePicker
                                                        maxWidth='100%'
                                                        value={DateService.removeTimezoneOffset(formData.CriticalDueDate)}
                                                        minDate={DateService.removeTimezoneOffset(formData.TargetEndDate)}
                                                        onSelectDate={date => changeHandlers.changeDatePicker(date, 'CriticalDueDate')}
                                                    />
                                                </div>

                                                {/* Empty field column for desktop alignment */}
                                                {!isMobile && <div className='two-fields-sidebyside-field'></div>}
                                            </div>
                                        </div>
                                    )}


                                    {/* Row 4 */}
                                    {
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {plannedTimeLabel}
                                                {actualTimeLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && plannedTimeLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrTextField
                                                        maxLength={3}
                                                        value={formData.PlannedTime}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField_number(newValue, 'PlannedTime')}
                                                    />
                                                </div>
                                                {isMobile && actualTimeLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrTextField
                                                        maxLength={3}
                                                        value={formData.ActualTime}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField_number(newValue, 'ActualTime')}
                                                    />
                                                </div>
                                            </div>
                                        </div>}

                                    {/* Actual Timelines - Info */}
                                    {hasActualTimeline && (
                                        <div className={styles.formField}>
                                            {isMobile !== true && (
                                                <div className='two-fields-sidebyside-r1'>
                                                    <div className='two-fields-sidebyside-lbl'>
                                                        Actual Timelines
                                                    </div>
                                                </div>
                                            )}

                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && (
                                                    <div className='two-fields-sidebyside-lbl'>
                                                        Actual Timelines
                                                    </div>
                                                )}

                                                <div className='two-fields-sidebyside-field'>
                                                    <span>
                                                        {actualTimelineText}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                </div>

                            </div>

                            {/* Additional Information Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Additional Information</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCustomFields
                                                    customFieldDfns={filteredCustomFieldDfns}
                                                    selectedItems={LookupService.customFieldsToDropdownWithText(formData.CustomFields)}
                                                    onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>


                        </div>
                    );
                }}
                loadEntity={id => dataServices.app03TaskService.read(id, true, true)}
                onAfterLoad={(task) => {
                    setShowArchived(task.Archived === true ? false : true);
                }}
                loadNewEntity={() => new App03Task(props.workflowID, props.newTaskStageID, props.epicID, props.taskTimeOption, props.defaultPlanningStrategy)}
                loadEntityValidations={() => new TaskFormValidations()}
                onValidateEntity={validateEntity}
                onCreate={t => dataServices.app03TaskService.create(t)}
                onUpdate={t => dataServices.app03TaskService.updatePut(t.ID, t)}
                //onUpdate={t => dataServices.app03TaskService.update(t.ID, t)}
                parentEntities={dataServices.app03TaskService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'CustomFields',
                        ParentIdProperty: 'App03TaskID',
                        ChildIdProperty: 'CustomFieldDefnID',
                        ChildService: dataServices.customFieldService
                    },
                ]}

            //onAfterCreate={t => dataServices.emailNotificationsService.sendEmailOnNewWorkflowTask(t.ID)}
            //onAfterUpdate2={t => dataServices.emailNotificationsService.sendEmailOnEditWorkflowTask(t.ID)}

            />
            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Sub Work Items</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <EntityList
                                entity={'Sub Work Item'}
                                hideHeading={true}
                                listAdditionalStyle={{
                                    minHeight: 'auto'
                                }}
                                apiConnected={true}
                                {...props}
                                entityName={{ Plural: "Sub Work Items", Singular: "Sub Work Item" }}
                                columns={[
                                    { key: '1', name: 'Name', fieldName: 'Name', minWidth: 100, maxWidth: 200, isResizable: true, isMultiline: true },
                                    { key: '2', name: 'Details', fieldName: 'Details', minWidth: 100, maxWidth: 300, isMultiline: true, isResizable: true, isCollapsible: true },
                                    { key: '3', name: 'Stage', fieldName: 'Stage', minWidth: 70, maxWidth: 100, isResizable: true, isCollapsible: true },
                                    { key: '4', name: 'Assigned To', fieldName: 'AssignedTo', minWidth: 70, maxWidth: 100, isResizable: true },
                                ]}
                                loadListItems={() => dataServices.app03SubTaskService.readSubTasksForList(props.entityId)}
                                mapEntitiesToListItems={subTasks => subTasks.map(st => (
                                    {
                                        key: st.ID,
                                        Name: st.Title,
                                        Details: st.Details,
                                        Stage: st.Stage,
                                        AssignedTo: st.AssignedToUser?.Title
                                    }
                                ))}
                                entityForm={(showForm, entityId, onSaved, onCancelled) =>
                                    <SubTaskForm
                                        {...props}
                                        app03TaskID={props.entityId}
                                        workflowUsers={workflowUsers}
                                        showForm={showForm}
                                        entityId={entityId}
                                        onSaved={onSaved}
                                        onCancelled={onCancelled} />
                                }
                                onCheckDelete={() => Promise.resolve([])}
                                onDelete={id => dataServices.app03SubTaskService.delete(id)}
                            />
                        </div>
                    </div>

                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Work Item Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginBottom: 20 }}>

                        <div style={{ minHeight: '120px' }}>
                            <EvidenceList
                                frameworkID={props.frameworkID}
                                taskID={props.entityId}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin() || userPermissions.UserIsCLSuperUser()}
                                isViewOnly={isViewOnlyPermission()}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>

            }

        </>
    );
};
